import type { Series, Unit } from '@pharmacia/core'
import { LinePlot } from './LinePlot.tsx'
import { Legend } from './plot-core.tsx'
import { colorFor } from './seriesColor.ts'
import { useSize } from './useSize.ts'

/**
 * Several small charts sharing an x axis but NOT a y scale.
 *
 * This is what a second y-axis should always have been. Pulse runs around
 * 80 and SpO2 around 97; drawn together on one axis the SpO2 line sits
 * above the pulse line and looks like the better number, which means
 * nothing. Given its own panel, each measure is read against its own
 * scale and compared by shape instead of by height.
 */
export function Multiples({ id, categories, series, unit }: {
  id: string
  categories: string[]
  series: Series[]
  unit: Unit
}) {
  // Series are grouped by the panel the server assigned; two measures share
  // a panel only when they genuinely share a scale.
  const panels: { title: string; series: Series[] }[] = []
  for (const s of series) {
    const title = s.panel ?? s.label
    const found = panels.find(p => p.title === title)
    if (found) found.series.push(s)
    else panels.push({ title, series: [s] })
  }

  return (
    <div className="viz-panels">
      {panels.map(p => (
        <Panel key={p.title} id={id} title={p.title} categories={categories}
               series={p.series} unit={p.series[0]?.unit ?? unit} />
      ))}
    </div>
  )
}

function Panel({ id, title, categories, series, unit }: {
  id: string; title: string; categories: string[]; series: Series[]; unit: Unit
}) {
  const { ref, width } = useSize<HTMLDivElement>()
  return (
    <div className="viz-panel">
      <h4>{title}</h4>
      <div ref={ref}>
        {width > 0 && (
          <LinePlot id={`${id}:${title}`} categories={categories} series={series}
                    unit={unit} width={width} height={150} />
        )}
      </div>
      {/* Per panel, not one legend for the chart: the same slot means a
          different measure in a different panel, so a shared legend would
          claim pulse and SpO2 were the same series. */}
      <Legend items={series.map(s => ({
        color: colorFor(`${id}:${title}`, s.key), label: s.label,
      }))} />
    </div>
  )
}
